import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const PER_PAGE = 24;
const SIGNED_TTL = 3600;

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

export default function Screenshots({ kidId, parentId, plan, features, deviceFilter }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState('');
  const [confirmItem, setConfirmItem] = useState(null);

  const totalPages = Math.ceil(total / PER_PAGE);

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    loadPage();
  }, [page, parentId, kidId, deviceFilter]); // eslint-disable-line

  async function loadPage() {
    if (!parentId) return;
    setLoading(true);
    setProgress(10);
    try {
      setProgress(30);
      let query = supabase
        .from('screenshots')
        .select('*', { count: 'exact' })
        .eq('parent_id', parentId);
      if (kidId) query = query.eq('kid_id', kidId);
      if (deviceFilter && deviceFilter !== 'all') query = query.eq('source', deviceFilter);
      const { data, count, error } = await query
        .order('timestamp', { ascending: false })
        .range(page * PER_PAGE, (page + 1) * PER_PAGE - 1);

      if (error) throw error;
      setProgress(60);

      // Generate signed URLs in batch
      const paths = (data || []).map(r => r.image_url);
      let signedMap = {};
      if (paths.length > 0) {
        const { data: signed } = await supabase.storage
          .from('parentguard')
          .createSignedUrls(paths, SIGNED_TTL);
        if (signed) {
          signed.forEach((s, i) => { signedMap[paths[i]] = s.signedUrl; });
        }
      }
      setProgress(90);

      const withUrls = (data || []).map(r => ({
        ...r,
        signedUrl: signedMap[r.image_url] || null,
      }));

      setItems(withUrls);
      setTotal(count || 0);
      setProgress(100);
    } catch (err) {
      console.error('Screenshots load error:', err);
      showToast('Failed to load screenshots');
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  async function handleDownload(item) {
    if (!item || !item.signedUrl) return;
    try {
      const res = await fetch(item.signedUrl);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `screenshot-${item.id}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      showToast('Download failed');
    }
  }

  async function handleDelete() {
    if (!confirmItem || !parentId) return;
    try {
      await supabase.storage.from('parentguard').remove([confirmItem.image_url]);
      await supabase.from('screenshots').delete().eq('id', confirmItem.id).eq('parent_id', parentId);
      setItems(prev => prev.filter(s => s.id !== confirmItem.id));
      setTotal(prev => prev - 1);
      showToast('Screenshot deleted');
    } catch {
      showToast('Delete failed');
    }
    setConfirmItem(null);
  }

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Screenshots <span style={{ fontSize: 14, color: 'var(--muted)' }}>({total})</span>
          </h1>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="loading-container">
          <div className="loading-icon">{'\uD83D\uDCF8'}</div>
          <div className="loading-title">Loading screenshots...</div>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="loading-percent">{progress}%</div>
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="empty-state">
          <div className="icon">📸</div>
          <div className="title">No screenshots yet</div>
          <div className="desc">
            Screenshots are captured automatically when the agent is running on your child's Mac.
          </div>
        </div>
      )}

      {/* Screenshot grid */}
      {!loading && items.length > 0 && (
        <div className="screenshot-grid">
          {items.map(item => (
            <div className="screenshot-card" key={item.id}>
              {item.signedUrl ? (
                <a href={item.signedUrl} target="_blank" rel="noreferrer">
                  <img src={item.signedUrl} alt={`Screenshot from ${formatTime(item.timestamp)}`} />
                </a>
              ) : (
                <div style={{ height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--border-light)', color: 'var(--muted)', fontSize: 13 }}>
                  Image unavailable
                </div>
              )}
              <div className="meta">{formatTime(item.timestamp)}</div>
              <div className="meta-actions">
                <button className="btn-sm" onClick={() => handleDownload(item)}>
                  Download
                </button>
                <button className="btn-sm btn-danger" onClick={() => setConfirmItem(item)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center', alignItems: 'center' }}>
          <button className="btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
            Prev
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button className="btn" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>
            Next
          </button>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmItem && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div className="card" style={{ maxWidth: 360, width: '90%', marginBottom: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Delete screenshot?</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              This will permanently remove this screenshot from storage.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-sm" onClick={() => setConfirmItem(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
